export const isDocumentMedia = (mediaFile: {
  isDocument?: boolean;
  url?: string;
}) => {
  return mediaFile.isDocument || mediaFile.url?.endsWith(".pdf");
};

/**
 * Mirrors VIDEO_EXTENSIONS in
 * apps/schedugram-server/src/app/utils/fileExtensions.ts. The server stores
 * QuickTime uploads as-is, so a narrower list reads a saved .mov back as an
 * image and skips every video rule. SKE-4631
 */
const VIDEO_EXTENSIONS = [
  "mp4",
  "mov",
  "qt",
  "avi",
  "flv",
  "wmv",
  "m4v",
  "3gpp",
  "3gp",
  "webm",
  "mpeg",
  "mpg",
];

const VIDEO_URL_REGEX = new RegExp(
  `\\.(${VIDEO_EXTENSIONS.join("|")})(\\?|#|$)`,
  "i"
);

/**
 * Whether a URL names a video file, ignoring any query string or fragment.
 * Anchored on the extension so a path segment such as "/a.mp4/cover.jpg" is
 * not mistaken for one.
 */
export const hasVideoExtension = (url?: string | null): boolean =>
  typeof url === "string" && VIDEO_URL_REGEX.test(url);

/**
 * The single answer to "is this media file a video".
 *
 * The explicit flag wins, and the extension decides when it is absent, which
 * is the normal state for a media file loaded back off a saved post.
 */
export const isVideoMedia = (
  mediaFile: { isVideo?: boolean; url?: string | null } | null | undefined
): boolean =>
  Boolean(mediaFile && (mediaFile.isVideo || hasVideoExtension(mediaFile.url)));

/**
 * A URL is a valid candidate for an image preview/thumbnail slot only when
 * it is not obviously a video file. Used to keep `.mp4`/`.mov` URLs out of
 * `<img src>` fallbacks. SKE-3646
 */
export const isImagePreviewUrl = (url?: string | null): url is string => {
  if (typeof url !== "string" || !url) {
    return false;
  }
  return !VIDEO_URL_REGEX.test(url);
};

type CoverFrameLike = { url?: string | null } | null | undefined;

type ThumbnailFallbackInput = {
  thumbnailUrl?: string | null;
  url?: string | null;
  originalUrl?: string | null;
  origUrl?: string | null;
  coverFrames?: CoverFrameLike[] | null;
};

/**
 * Pick a thumbnail URL suitable for `<img>` rendering. Prefers an explicit
 * thumbnail, then a coverFrame, then originalUrl, never returning a video
 * URL. Returns undefined when no valid image candidate is available so
 * callers can show a placeholder/spinner instead of a broken image.
 *
 * SKE-3646: replaces the old `thumbnailUrl || url` fallback that could
 * plant an `.mp4` URL into an image src slot.
 */
export const pickThumbnailFallback = (
  asset: ThumbnailFallbackInput | null | undefined
): string | undefined => {
  if (!asset) {
    return undefined;
  }
  if (isImagePreviewUrl(asset.thumbnailUrl)) {
    return asset.thumbnailUrl;
  }
  if (Array.isArray(asset.coverFrames)) {
    for (const frame of asset.coverFrames) {
      if (frame && isImagePreviewUrl(frame.url)) {
        return frame.url;
      }
    }
  }
  if (isImagePreviewUrl(asset.originalUrl)) {
    return asset.originalUrl;
  }
  if (isImagePreviewUrl(asset.origUrl)) {
    return asset.origUrl;
  }
  if (isImagePreviewUrl(asset.url)) {
    return asset.url;
  }
  return undefined;
};
